import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  toast,
} from '@mlm/ui';
import {
  createSupplierPayment,
  deleteSupplierInvoice,
  fetchReport,
  getSupplierInvoice,
  listSupplierInvoicePayments,
  listFundsAccounts,
  listSupplierInvoices,
  listSupplierPayments,
  listSuppliers,
  postSupplierInvoice,
  voidSupplierInvoice,
  voidSupplierPayment,
  type InvoiceStatus,
  type ReportParams,
  type ReportResult,
  type SupplierInvoiceDetail,
  type SupplierInvoiceRow,
  type SupplierPaymentRow,
} from '../../lib/api';
import { defaultFundsAccountFor } from '@mlm/shared';
import { downloadReport, type ReportExportMeta } from '../../lib/report-export';
import { ReportExportButtons } from '../../components/report-export-buttons';
import { useHasPerm } from '../../lib/auth-store';
import { Money } from './_shared';
import { BillDetailPanel, BillEditorDialog } from './bill-editor';
import { SupplierReturnsPanel } from './return-editor';

/**
 * Payables — the LIVE accounts-payable workspace (QuickBooks "Bills" feel):
 * aging summary off the `ap-aging` report, the supplier-bill list with
 * three-way-match badges, bill entry, payment entry/void, and a per-supplier
 * ledger backed by the `supplier-ledger` report. Same visual language as the
 * sample-data ap.tsx page, fed by real data.
 *
 * Route-level view perm + the RequireAccounting wrapper live in the router.
 */

// ── display helpers (server money stays a decimal STRING; Number() below is
//    display-only formatting/proportions, never stored math) ─────────────────

function peso(v: string | number | null | undefined): string {
  const n = Number(v ?? 0);
  return `₱${(Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** Local-timezone YYYY-MM-DD for `<input type="date">` defaults. */
function todayInput(): string {
  return new Date().toLocaleDateString('en-CA');
}

function monthStartInput(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA');
}

/**
 * Date-only strings parse as UTC midnight server-side (8 AM Manila cutoff);
 * send local day bounds as ISO datetimes instead.
 */
function dayBounds(from: string, to: string): { from?: string; to?: string } {
  return {
    ...(from ? { from: new Date(`${from}T00:00:00`).toISOString() } : {}),
    ...(to ? { to: new Date(`${to}T23:59:59.999`).toISOString() } : {}),
  };
}

/** The API's ceiling; when a range hits it the footer total is only partial. */
const SUPPLIER_PAYMENTS_LIMIT = 500;

/** Outstanding balance of a bill (total − paid − RTS credits) — display-only. */
function balanceOf(inv: SupplierInvoiceRow): number {
  return Number(inv.total) - Number(inv.amountPaid) - Number(inv.creditApplied ?? 0);
}

/** The server's moneyStringSchema takes no thousands separators. */
function sanitizeMoney(v: string): string {
  return v.replace(/,/g, '').trim();
}

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

const ERROR_COPY: Record<string, string> = {
  duplicate_supplier_invoice: 'A bill with this number already exists for this supplier.',
  invoice_has_payments: 'This bill has posted payments — void those payments first.',
  invoice_has_credits: 'A return to supplier is applied to this bill — void that return first.',
  bill_link_required:
    'This PO already has a posted bill — link the return to that bill so the credit lands on it.',
  over_return: 'More units than were received on that PO line.',
  credit_exceeds_bill: 'Returns against this bill would exceed the bill total.',
  branch_not_house: 'Returns leave a house branch — a stockist branch cannot be the source.',
  out_of_stock: 'The branch no longer holds that many units.',
  cost_required: 'This product has no cost — enter a unit cost for the return.',
  zero_value_return: 'The return has no value.',
  po_not_received: 'Nothing has been received on that PO yet.',
  return_has_refunds: 'A supplier refund is recorded against this return — void it first.',
  already_void: 'This document is already void.',
  invoice_void: 'This bill is void.',
  invoice_already_paid: 'This bill is already fully paid.',
  payment_exceeds_outstanding: 'Amount is more than the remaining balance.',
  invalid_amount: 'Enter an amount greater than zero.',
  invalid_vat_amount: 'VAT must be less than the bill total.',
  po_supplier_mismatch: 'That PO belongs to a different supplier.',
  po_not_found: 'Purchase order not found.',
  supplier_not_found: 'Supplier not found.',
  accounting_disabled: 'Accounting is switched off for this tenant.',
  invoice_not_draft: 'Only draft bills can be edited or posted.',
  invoice_draft: "Drafts can't be voided — delete the draft instead.",
  invoice_not_posted: 'Post the bill before recording a payment.',
  draft_has_no_lines: 'Add at least one line before posting.',
  receipt_already_billed: 'One of the selected receipts is already on another bill.',
  po_cancelled: 'The linked PO was cancelled — edit the draft and unlink it before posting.',
  validation_error:
    'A field has an invalid value — check quantities and amounts (up to 4 decimals).',
};

function friendly(error: unknown, fallback: string): string {
  const code = errText(error);
  if (!code) return fallback;
  // `over_return:<lineId>` / `out_of_stock:<id>` / `cost_required:<id>` carry an id suffix.
  const bare = code.split(':')[0]!;
  return ERROR_COPY[code] ?? ERROR_COPY[bare] ?? code;
}

const SELECT_CLS = 'h-9 rounded-md border border-input bg-background px-3 text-sm';

const PAYMENT_METHODS: Array<[string, string]> = [
  ['cash', 'Cash'],
  ['bank_transfer', 'Bank transfer'],
  ['gcash', 'GCash'],
  ['check', 'Check'],
  ['other', 'Other'],
];

// ── badges ──────────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<InvoiceStatus, 'secondary' | 'warning' | 'success' | 'muted'> = {
  draft: 'muted',
  open: 'secondary',
  partial: 'warning',
  paid: 'success',
  void: 'muted',
};

function StatusBadge({ status }: { status: InvoiceStatus }) {
  return <Badge variant={STATUS_VARIANT[status]}>{status}</Badge>;
}

const BASIS_LABEL: Record<string, string> = {
  two_way: 'vs the whole PO',
  three_way: 'vs linked receipts',
};

function MatchBadge({ inv }: { inv: SupplierInvoiceRow }) {
  // Drafts haven't been matched yet — posting computes it.
  if (inv.status === 'draft') return <span className="text-xs text-muted-foreground">—</span>;
  const basis = inv.matchBasis ? ` (${BASIS_LABEL[inv.matchBasis] ?? inv.matchBasis})` : '';
  if (inv.matchStatus === 'matched') {
    return (
      <Badge variant="success" title={`Matched${basis}`}>
        matched
      </Badge>
    );
  }
  if (inv.matchStatus === 'variance') {
    return (
      <Badge variant="warning" title={`Variance ${peso(inv.matchVariance)}${basis}`}>
        variance
      </Badge>
    );
  }
  return <Badge variant="muted">no PO</Badge>;
}

// ── payments register (every supplier payment, newest first) ────────────────

/**
 * What went OUT to suppliers and when — the cross-supplier view Payables was
 * missing (per-bill payments live in the bill row, per-supplier history in the
 * ledger). Bill payments settle a liability; they are not expenses, so this
 * lives here and never on the Expenses page.
 */
function SupplierPaymentsPanel({ suppliers }: { suppliers: Array<{ id: string; name: string }> }) {
  const [from, setFrom] = useState(monthStartInput());
  const [to, setTo] = useState(todayInput());
  const [supplierId, setSupplierId] = useState('');
  const range = dayBounds(from, to);
  const q = useQuery({
    queryKey: ['supplier-payments', range, supplierId],
    queryFn: () =>
      listSupplierPayments({
        ...range,
        ...(supplierId ? { supplierId } : {}),
        limit: SUPPLIER_PAYMENTS_LIMIT,
      }),
  });
  const rows = q.data ?? [];
  const postedTotal = rows
    .filter((p) => p.status === 'posted')
    .reduce((s, p) => s + Number(p.amount), 0);
  const methodLabel = new Map(PAYMENT_METHODS);

  return (
    <Card>
      <CardHeader className="gap-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <CardTitle>
            Payments{' '}
            <span className="text-sm font-normal text-muted-foreground">({rows.length})</span>
          </CardTitle>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">From</label>
              <Input
                type="date"
                className="h-9 w-40"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">To</label>
              <Input
                type="date"
                className="h-9 w-40"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Supplier</label>
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                className={`${SELECT_CLS} min-w-[12rem]`}
              >
                <option value="">All suppliers</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Every payment recorded against a supplier bill, newest first. To see one supplier&apos;s
          bills and payments as a running balance, open its Ledger from the bills table.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {q.isLoading ? (
          <p className="p-4 text-sm text-muted-foreground">Loading…</p>
        ) : q.error ? (
          <p className="p-4 text-sm text-destructive">
            {friendly(q.error, 'Could not load supplier payments.')}
          </p>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No payments in this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">SPAY #</th>
                  <th className="px-4 py-2">Supplier</th>
                  <th className="px-4 py-2">Bill #</th>
                  <th className="px-4 py-2">Method</th>
                  <th className="px-4 py-2">Ref</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const voided = p.status === 'void';
                  return (
                    <tr
                      key={p.id}
                      className={`border-b last:border-0 hover:bg-muted/30 ${voided ? 'opacity-60' : ''}`}
                      title={voided && p.voidReason ? `Voided: ${p.voidReason}` : undefined}
                    >
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {shortDate(p.paidAt)}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{p.docNo}</td>
                      <td className="px-4 py-2">{p.supplierName}</td>
                      <td className="px-4 py-2 font-mono text-xs">{p.invoiceNo}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {methodLabel.get(p.method) ?? p.method}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                        {p.reference ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Money>{peso(p.amount)}</Money>
                      </td>
                      <td className="px-4 py-2">
                        <Badge variant={voided ? 'muted' : 'success'}>{p.status}</Badge>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 font-semibold">
                  <td className="px-4 py-2" colSpan={6}>
                    Total paid to suppliers (posted
                    {rows.length >= SUPPLIER_PAYMENTS_LIMIT ? ', listed rows only' : ''})
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Money>{peso(postedTotal)}</Money>
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
            {rows.length >= SUPPLIER_PAYMENTS_LIMIT ? (
              <p className="border-t px-4 py-2 text-xs text-muted-foreground">
                Showing the newest {SUPPLIER_PAYMENTS_LIMIT} payments — narrow the date range for a
                complete total.
              </p>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── aging summary (live `ap-aging` report) ──────────────────────────────────

/** Single-hue ordinal ramp, light → dark with age (mirrors sample-data's). */
const AGING_RAMP = ['#e2a53d', '#c68a22', '#a06d0d', '#7a530a', '#513607'];

function AgingSummary() {
  const q = useQuery({
    queryKey: ['ap-aging-report'],
    queryFn: () => fetchReport('ap-aging', {}),
  });
  const summary = q.data?.summary ?? [];
  const buckets = summary.filter((s) => s.label !== 'Total payable' && s.label !== 'Credits');
  const totalStr = summary.find((s) => s.label === 'Total payable')?.value ?? '0';
  const totalNum = Number(totalStr);
  const overdue = buckets.slice(1).reduce((s, b) => s + Number(b.value), 0);
  const segments = buckets
    .map((b, i) => ({ ...b, num: Number(b.value), color: AGING_RAMP[i % AGING_RAMP.length] }))
    .filter((b) => b.num > 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Aging</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {q.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : q.error ? (
          <p className="text-sm text-destructive">Could not load the aging summary.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
              {buckets.map((b, i) => (
                <div key={b.label}>
                  <div className="flex items-center gap-1.5">
                    <span
                      aria-hidden
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: AGING_RAMP[i % AGING_RAMP.length] }}
                    />
                    <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                      {b.label}
                    </p>
                  </div>
                  <p className="mt-1 text-lg font-semibold tabular-nums">{peso(b.value)}</p>
                </div>
              ))}
            </div>
            {totalNum > 0 ? (
              <div className="mt-4 flex h-2.5 w-full gap-[2px] overflow-hidden rounded-full">
                {segments.map((b, i) => (
                  <div
                    key={b.label}
                    title={`${b.label}: ${peso(b.value)}`}
                    style={{
                      width: `${(b.num / totalNum) * 100}%`,
                      background: b.color,
                      borderTopLeftRadius: i === 0 ? 9999 : 0,
                      borderBottomLeftRadius: i === 0 ? 9999 : 0,
                      borderTopRightRadius: i === segments.length - 1 ? 9999 : 0,
                      borderBottomRightRadius: i === segments.length - 1 ? 9999 : 0,
                    }}
                  />
                ))}
              </div>
            ) : null}
            <p className="mt-3 border-t pt-3 text-xs text-muted-foreground">
              <span className="font-medium tabular-nums text-debit">{peso(overdue)}</span> of{' '}
              <span className="font-medium tabular-nums">{peso(totalStr)}</span> is past due.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── pay dialog ──────────────────────────────────────────────────────────────

function PayDialog({
  invoice,
  onClose,
  onPaid,
}: {
  invoice: SupplierInvoiceRow;
  onClose: () => void;
  onPaid: () => void;
}) {
  const outstanding = balanceOf(invoice);
  const [amount, setAmount] = useState(outstanding > 0 ? outstanding.toFixed(2) : '');
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState(todayInput());
  const [memo, setMemo] = useState('');
  // One idempotency key per open dialog — a double-click can't pay twice.
  const [clientRequestId] = useState(() => crypto.randomUUID());

  // Cash/Bank register. The default follows the payment method until the user
  // picks for themselves, so switching method to GCash re-points at the wallet.
  const fundsQ = useQuery({
    queryKey: ['funds-accounts', 'active'],
    queryFn: () => listFundsAccounts(true),
  });
  // Branchless accounts only: `supplier_payments` has no branch column at all,
  // so a branch-bound till can never be a bill payment's source
  // (`assertFundsAccountUsable` raises `funds_account_branch_required`).
  // Offering one would auto-select an account the server always refuses.
  const fundsAccounts = (fundsQ.data?.rows ?? []).filter((a) => a.branchId === null);
  const fundsRequired = fundsQ.data?.required ?? false;
  const [fundsTouched, setFundsTouched] = useState(false);
  const [fundsAccountId, setFundsAccountId] = useState('');
  const suggested = defaultFundsAccountFor(fundsAccounts, { method, branchId: null }) ?? '';
  const effectiveFundsAccountId = fundsTouched ? fundsAccountId : suggested;

  const pay = useMutation({
    mutationFn: () =>
      createSupplierPayment(invoice.id, {
        amount: sanitizeMoney(amount),
        method,
        reference: reference.trim() || null,
        paidAt: paidAt || null,
        memo: memo.trim() || null,
        clientRequestId,
        fundsAccountId: effectiveFundsAccountId || null,
      }),
    meta: {
      successMessage: 'Payment recorded.',
      silentErrorCodes: ['payment_exceeds_outstanding', 'invalid_amount', 'invoice_already_paid'],
    },
    onSuccess: onPaid,
  });

  const canSubmit =
    !!amount.trim() && !!method && !pay.isPending && (!fundsRequired || !!effectiveFundsAccountId);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Pay bill {invoice.invoiceNo}</DialogTitle>
          <DialogDescription>
            {invoice.supplierName} · outstanding{' '}
            <span className="font-medium tabular-nums text-foreground">{peso(outstanding)}</span>
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) pay.mutate();
          }}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Amount</label>
              <Input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Method</label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className={`${SELECT_CLS} w-full`}
              >
                {PAYMENT_METHODS.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {(fundsAccounts.length > 0 || fundsRequired) && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Paid from{fundsRequired ? '' : ' (optional)'}
                </label>
                <select
                  className={SELECT_CLS}
                  value={effectiveFundsAccountId}
                  onChange={(e) => {
                    setFundsTouched(true);
                    setFundsAccountId(e.target.value);
                  }}
                >
                  <option value="">— none —</option>
                  {fundsAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Reference (optional)</label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Check no., transfer ref…"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Date</label>
              <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Memo</label>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              className="min-h-14 w-full rounded-md border border-input bg-background p-2 text-sm"
            />
          </div>

          {pay.error != null && (
            <p className="text-sm text-destructive">
              {friendly(pay.error, 'Failed to record the payment.')}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {pay.isPending ? 'Recording…' : 'Record payment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── payments expansion panel ────────────────────────────────────────────────

function PaymentsPanel({
  invoice,
  canPay,
  onChanged,
}: {
  invoice: SupplierInvoiceRow;
  canPay: boolean;
  onChanged: () => void;
}) {
  const q = useQuery({
    queryKey: ['supplier-invoice-payments', invoice.id],
    queryFn: () => listSupplierInvoicePayments(invoice.id),
  });
  const voidPay = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => voidSupplierPayment(id, reason),
    meta: { successMessage: 'Payment voided.' },
    onSuccess: onChanged,
  });

  function askVoid(p: SupplierPaymentRow) {
    const reason = window.prompt(`Void payment ${p.docNo}? Enter a reason:`)?.trim();
    if (!reason) return;
    if (reason.length < 3) {
      toast.error('Reason must be at least 3 characters.');
      return;
    }
    voidPay.mutate({ id: p.id, reason });
  }

  const rows = q.data ?? [];

  return (
    <div className="bg-muted/20 px-6 py-3">
      <p className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
        Payments on {invoice.invoiceNo}
      </p>
      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
      ) : (
        <table className="w-full max-w-2xl text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-1 pr-3">Doc no.</th>
              <th className="py-1 pr-3">Date</th>
              <th className="py-1 pr-3">Method</th>
              <th className="py-1 pr-3">Reference</th>
              <th className="py-1 pr-3 text-right">Amount</th>
              <th className="py-1 pr-3">Status</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className={`border-t ${p.status === 'void' ? 'opacity-60' : ''}`}>
                <td className="py-1.5 pr-3 font-mono text-xs">{p.docNo}</td>
                <td className="py-1.5 pr-3 text-xs text-muted-foreground">{shortDate(p.paidAt)}</td>
                <td className="py-1.5 pr-3 text-xs">{p.method}</td>
                <td className="py-1.5 pr-3 text-xs text-muted-foreground">{p.reference ?? '—'}</td>
                <td className="py-1.5 pr-3 text-right">
                  <Money>{peso(p.amount)}</Money>
                </td>
                <td className="py-1.5 pr-3">
                  <Badge variant={p.status === 'void' ? 'muted' : 'success'}>{p.status}</Badge>
                </td>
                <td className="py-1.5 text-right">
                  {canPay && p.status === 'posted' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      disabled={voidPay.isPending}
                      onClick={() => askVoid(p)}
                    >
                      Void
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── per-supplier ledger panel (live `supplier-ledger` report) ───────────────

function LedgerPanel({
  supplier,
  onClose,
}: {
  supplier: { id: string; name: string };
  onClose: () => void;
}) {
  const params: ReportParams & { supplierId: string } = { supplierId: supplier.id };
  const q = useQuery({
    queryKey: ['supplier-ledger-report', supplier.id],
    queryFn: () => fetchReport('supplier-ledger', params),
  });
  // The supplier rides in the TITLE, which is also what the filename is slugged
  // from — a folder full of `report-supplier-ledger-*.pdf` would be unusable.
  const meta: ReportExportMeta = {
    title: `Supplier Ledger — ${supplier.name}`,
    sheetName: 'Ledger',
    subtitle: ['All bills, payments, returns and refunds to date'],
  };

  function renderCell(col: ReportResult['columns'][number], v: string | number | null) {
    if (col.key === 'date') return shortDate(v == null ? null : String(v));
    if (col.align === 'right') {
      if ((col.key === 'charges' || col.key === 'payments') && Number(v ?? 0) === 0) return '—';
      return <Money>{peso(v)}</Money>;
    }
    return v ?? '—';
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">
          Supplier ledger{' '}
          <span className="text-sm font-normal text-muted-foreground">— {supplier.name}</span>
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <ReportExportButtons
            result={q.data}
            meta={meta}
            onCsv={() =>
              downloadReport('supplier-ledger', params, 'csv', { ...meta, result: q.data })
            }
          />
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {q.isLoading ? (
          <p className="p-4 text-sm text-muted-foreground">Loading…</p>
        ) : q.error ? (
          <p className="p-4 text-sm text-destructive">Could not load the ledger.</p>
        ) : !q.data?.rows.length ? (
          <p className="p-4 text-sm text-muted-foreground">
            No bills or payments for this supplier.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs text-muted-foreground">
                  <tr>
                    {q.data.columns.map((c) => (
                      <th
                        key={c.key}
                        className={`px-4 py-2 ${c.align === 'right' ? 'text-right' : ''}`}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {q.data.rows.map((row, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                      {q.data!.columns.map((c) => (
                        <td
                          key={c.key}
                          className={`px-4 py-1.5 text-xs ${c.align === 'right' ? 'text-right' : ''}`}
                        >
                          {renderCell(c, row[c.key] ?? null)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {q.data.summary?.length ? (
              <div className="flex flex-wrap gap-x-6 gap-y-1 border-t px-4 py-3 text-xs">
                {q.data.summary.map((s) => (
                  <span key={s.label}>
                    <span className="text-muted-foreground">{s.label}: </span>
                    <span className="font-medium tabular-nums">{peso(s.value)}</span>
                  </span>
                ))}
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── page ────────────────────────────────────────────────────────────────────

const STATUS_FILTERS: Array<InvoiceStatus | 'all'> = [
  'all',
  'draft',
  'open',
  'partial',
  'paid',
  'void',
];

export function PayablesPage() {
  const qc = useQueryClient();
  const canWriteBill = useHasPerm('supplier_invoice.write');
  const canPay = useHasPerm('supplier_payment.write');

  const [status, setStatus] = useState<InvoiceStatus | 'all'>('all');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [editor, setEditor] = useState<
    { mode: 'create' } | { mode: 'edit'; detail: SupplierInvoiceDetail } | null
  >(null);
  const [openDetailId, setOpenDetailId] = useState<string | null>(null);
  const [payFor, setPayFor] = useState<SupplierInvoiceRow | null>(null);
  const [openPaymentsId, setOpenPaymentsId] = useState<string | null>(null);
  const [ledgerFor, setLedgerFor] = useState<{ id: string; name: string } | null>(null);

  const suppliersQ = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: () => listSuppliers(true),
  });
  const invoicesQ = useQuery({
    queryKey: ['supplier-invoices', status, supplierFilter],
    queryFn: () =>
      listSupplierInvoices({
        ...(status !== 'all' ? { status } : {}),
        ...(supplierFilter ? { supplierId: supplierFilter } : {}),
      }),
  });

  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['supplier-invoices'] });
    void qc.invalidateQueries({ queryKey: ['ap-aging-report'] });
    void qc.invalidateQueries({ queryKey: ['supplier-invoice-payments'] });
    void qc.invalidateQueries({ queryKey: ['supplier-payments'] });
    void qc.invalidateQueries({ queryKey: ['supplier-ledger-report'] });
    void qc.invalidateQueries({ queryKey: ['po-billing-summary'] });
    void qc.invalidateQueries({ queryKey: ['supplier-invoice-detail'] });
    void qc.invalidateQueries({ queryKey: ['po-billable-receipts'] });
    void qc.invalidateQueries({ queryKey: ['supplier-returns'] });
  };

  // Draft actions — post computes the match; delete is the draft's "void".
  const postDraft = useMutation({
    mutationFn: (id: string) => postSupplierInvoice(id),
    meta: { successMessage: 'Bill posted.' },
    onSuccess: refreshAll,
  });
  const deleteDraft = useMutation({
    mutationFn: (id: string) => deleteSupplierInvoice(id),
    meta: { successMessage: 'Draft deleted.' },
    onSuccess: refreshAll,
  });
  const openEditor = useMutation({
    mutationFn: (id: string) => getSupplierInvoice(id),
    onSuccess: (detail) => setEditor({ mode: 'edit', detail }),
  });

  function askDeleteDraft(inv: SupplierInvoiceRow) {
    if (window.confirm(`Delete draft ${inv.invoiceNo}? This cannot be undone.`)) {
      deleteDraft.mutate(inv.id);
    }
  }

  const voidBill = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => voidSupplierInvoice(id, reason),
    meta: { successMessage: 'Bill voided.' },
    onSuccess: refreshAll,
  });

  function askVoidBill(inv: SupplierInvoiceRow) {
    const reason = window.prompt(`Void bill ${inv.invoiceNo}? Enter a reason:`)?.trim();
    if (!reason) return;
    if (reason.length < 3) {
      toast.error('Reason must be at least 3 characters.');
      return;
    }
    voidBill.mutate({ id: inv.id, reason });
  }

  const rows = invoicesQ.data ?? [];
  // Display-only footer: sum of non-void outstanding balances in this view.
  const shownBalance = rows
    .filter((r) => r.status !== 'void' && r.status !== 'draft')
    .reduce((s, r) => s + balanceOf(r), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Payables</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Supplier bills, payments and aging — what the company owes and when.
          </p>
        </div>
        {canWriteBill ? (
          <Button onClick={() => setEditor({ mode: 'create' })}>+ New bill</Button>
        ) : null}
      </div>

      <AgingSummary />

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as InvoiceStatus | 'all')}
            className={SELECT_CLS}
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All statuses' : s}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Supplier</label>
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className={`${SELECT_CLS} min-w-[14rem]`}
          >
            <option value="">All suppliers</option>
            {(suppliersQ.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            Bills <span className="text-sm font-normal text-muted-foreground">({rows.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {invoicesQ.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading…</p>
          ) : invoicesQ.error ? (
            <p className="p-4 text-sm text-destructive">Could not load bills.</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No bills match these filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Bill no.</th>
                    <th className="px-4 py-2">Supplier</th>
                    <th className="px-4 py-2">PO</th>
                    <th className="px-4 py-2">Bill date</th>
                    <th className="px-4 py-2">Due date</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    <th className="px-4 py-2 text-right">Paid</th>
                    <th className="px-4 py-2 text-right">Credits</th>
                    <th className="px-4 py-2 text-right">Balance</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Match</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((inv) => {
                    const overdue =
                      (inv.status === 'open' || inv.status === 'partial') &&
                      new Date(inv.dueDate).getTime() < Date.now();
                    return (
                      <Fragment key={inv.id}>
                        <tr
                          className={`border-b last:border-0 hover:bg-muted/30 ${
                            inv.status === 'void' ? 'opacity-60' : ''
                          }`}
                        >
                          <td className="px-4 py-2 font-mono text-xs">{inv.invoiceNo}</td>
                          <td className="px-4 py-2">{inv.supplierName}</td>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                            {inv.poNumber ?? '—'}
                          </td>
                          <td className="px-4 py-2 text-xs text-muted-foreground">
                            {shortDate(inv.invoiceDate)}
                          </td>
                          <td
                            className={`px-4 py-2 text-xs ${
                              overdue ? 'font-medium text-debit' : 'text-muted-foreground'
                            }`}
                          >
                            {shortDate(inv.dueDate)}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <Money>{peso(inv.total)}</Money>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <Money>{peso(inv.amountPaid)}</Money>
                          </td>
                          <td className="px-4 py-2 text-right">
                            {Number(inv.creditApplied) > 0 ? (
                              <Money>{peso(inv.creditApplied)}</Money>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {inv.status === 'void' ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (
                              <Money>{peso(balanceOf(inv))}</Money>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <StatusBadge status={inv.status} />
                          </td>
                          <td className="px-4 py-2">
                            <MatchBadge inv={inv} />
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex justify-end gap-1">
                              {inv.status === 'draft' ? (
                                canWriteBill ? (
                                  <>
                                    <Button
                                      size="sm"
                                      disabled={openEditor.isPending}
                                      onClick={() => openEditor.mutate(inv.id)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={postDraft.isPending}
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            `Post bill ${inv.invoiceNo}? Posted bills can only be voided, not edited.`,
                                          )
                                        )
                                          postDraft.mutate(inv.id);
                                      }}
                                    >
                                      Post
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-destructive"
                                      disabled={deleteDraft.isPending}
                                      onClick={() => askDeleteDraft(inv)}
                                    >
                                      Delete
                                    </Button>
                                  </>
                                ) : null
                              ) : (
                                <>
                                  {canPay && (inv.status === 'open' || inv.status === 'partial') ? (
                                    <Button size="sm" onClick={() => setPayFor(inv)}>
                                      Pay
                                    </Button>
                                  ) : null}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                      setOpenPaymentsId(openPaymentsId === inv.id ? null : inv.id)
                                    }
                                  >
                                    {openPaymentsId === inv.id ? 'Hide' : 'Payments'}
                                  </Button>
                                </>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  setOpenDetailId(openDetailId === inv.id ? null : inv.id)
                                }
                              >
                                {openDetailId === inv.id ? 'Hide details' : 'Details'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  setLedgerFor({ id: inv.supplierId, name: inv.supplierName })
                                }
                              >
                                Ledger
                              </Button>
                              {canWriteBill && inv.status !== 'void' && inv.status !== 'draft' ? (
                                <span
                                  title={
                                    Number(inv.amountPaid) > 0
                                      ? 'Void this bill’s payments first — money is already applied.'
                                      : Number(inv.creditApplied) > 0
                                        ? 'Void the linked supplier return first — a credit is applied.'
                                        : undefined
                                  }
                                >
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-destructive"
                                    disabled={
                                      Number(inv.amountPaid) > 0 ||
                                      Number(inv.creditApplied) > 0 ||
                                      voidBill.isPending
                                    }
                                    onClick={() => askVoidBill(inv)}
                                  >
                                    Void
                                  </Button>
                                </span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                        {openPaymentsId === inv.id ? (
                          <tr className="border-b last:border-0">
                            <td colSpan={12} className="p-0">
                              <PaymentsPanel invoice={inv} canPay={canPay} onChanged={refreshAll} />
                            </td>
                          </tr>
                        ) : null}
                        {openDetailId === inv.id ? (
                          <tr className="border-b last:border-0">
                            <td colSpan={12} className="p-0">
                              <BillDetailPanel invoiceId={inv.id} />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                  <tr className="border-t-2 font-semibold">
                    <td className="px-4 py-2" colSpan={8}>
                      Outstanding in this view
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Money>{peso(shownBalance)}</Money>
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <SupplierPaymentsPanel suppliers={suppliersQ.data ?? []} />

      <SupplierReturnsPanel suppliers={suppliersQ.data ?? []} onChanged={refreshAll} />

      {ledgerFor ? <LedgerPanel supplier={ledgerFor} onClose={() => setLedgerFor(null)} /> : null}

      {editor ? (
        <BillEditorDialog
          suppliers={suppliersQ.data ?? []}
          initial={editor.mode === 'edit' ? editor.detail : undefined}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            refreshAll();
          }}
        />
      ) : null}

      {payFor ? (
        <PayDialog
          invoice={payFor}
          onClose={() => setPayFor(null)}
          onPaid={() => {
            setPayFor(null);
            refreshAll();
          }}
        />
      ) : null}
    </div>
  );
}
